import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { IconComponent } from '../../atoms/icon/icon.component';
import { ModalFunctionComponent } from '../../molecules/modal-function/modal-function.component';
import { ButtonComponent } from '../../molecules/button/button.component';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { InpageSearchComponent } from '../inpage-search/inpage-search.component';
import { ModalFunctionType } from '../../../shared/enums/modal-function-type.enums';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import ImageType from '../../../shared/enums/image-type.enums';
import { Colors, Headlines, Sizes } from '../../../shared/enums/shared.enums';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HeaderStage } from '../../../shared/interfaces/header-stage.interface';
import { ActionType } from '../../../shared/enums/action-type.enums';
import {
  CaasFormField,
  CaasLink,
  CaasSimpleField,
  CaasStage,
} from '../../../shared/interfaces/caas/caas.interface';
import { Router } from '@angular/router';
import { ImageComponent } from '../../atoms/image/image.component';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { LinkComponent } from '../../atoms/link/link.component';
import { HrefType } from '../../../shared/enums/href-type.enums';
import LinkType from '../../../shared/enums/link-type.enums';
import { ImagesService } from '../../../services/images.service';
import { CapitalizePipe } from '../../capitalize.pipe';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-header-stage',
  standalone: true,
  imports: [
    NgxSkeletonLoaderModule,
    CommonModule,
    IconComponent,
    ModalFunctionComponent,
    ButtonComponent,
    InpageSearchComponent,
    ImageComponent,
    LinkComponent,
    CapitalizePipe,
  ],
  templateUrl: './header-stage.component.html',
  styleUrls: ['./header-stage.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class HeaderStageComponent implements OnChanges {
  @Output() handleInputChange = new EventEmitter<string>();
  @Input() caasData?: CaasStage;

  @Input() set content(value: HeaderStage) {
    this._content = { ...this.defaultContent, ...value };
  }

  constructor(
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef,
    private readonly caasRoutesService: CaasRoutesService,
    private readonly caasBodytextService: CaasBodytextService,
    private readonly imagesService: ImagesService,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  get content(): HeaderStage {
    return this._content;
  }

  readonly defaultContent: HeaderStage = {
    type: 'icon',
    actionType: undefined,
    searchPlaceholder: '',
    showBadge: true,
    iconBadgeColor: Colors.GREEN,
    iconBadgeIconName: 'desktop',
    iconBadgeIconColor: Colors.WHITE,
    iconBadgeIconSize: Sizes.XXLARGE,
    imageBadgeSrc: '',
    imageBadgeTitle: '',
    reverse: false,
    imageSrc: '',
    imageSlot2Src: '',
    imageSlot3Src: '',
    imageAlt: '',
    imageSlot2Alt: '',
    imageSlot3Alt: '',
    imageTitle: '',
    imageSlot2Title: '',
    imageSlot3Title: '',
    headline: '',
    paragraph: '',
    imageMirror: false,
    videoSrc: '' as unknown as SafeResourceUrl, // Handle `SafeResourceUrl` type
    videoId: '',
    videoTitle: '',
    thumbnailSrc: '',
    headlineSize: Headlines.H1,
    showPlayButton: false,
    playButtonSize: Sizes.MEDIUM,
    buttonLabel: '',
    buttonTitle: '',
    buttonType: ButtonType.PRIMARY,
    buttonIcon: undefined,
    optionalButtonLabel: '',
    optionalButtonTitle: '',
    optionalHref: '',
    optionalTarget: '_self',
    optionalHrefType: undefined,
    optionalLinkType: LinkType.PRIMARY_ARROW,
    optionalVideo: undefined,
    imageObjectPosition: 'center',
    href: '',
    target: '_self',
  };

  private _content: HeaderStage = this.defaultContent;

  public isLoading = true;

  Headlines = Headlines;
  Sizes = Sizes;
  ButtonType = ButtonType;
  HrefType = HrefType;
  LinkType = LinkType;
  readonly ModalFunctionType = ModalFunctionType;
  readonly ImageType = ImageType;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      this.setCaasContent();
      if (isPlatformBrowser(this.platformId)) {
        this.isLoading = false;
      }
    }
    this.cdr.detectChanges();
  }

  setCaasContent() {
    this.content = {
      headline: this.getCaasHeadline(),
      paragraph: this.getCaasParagraph(),
      imageSrc: this.getCaasSlot1Src(),
      imageSlot2Src: this.getCaasSlot2Src(),
      imageSlot3Src: this.getCaasSlot3Src(),
      imageBadgeSrc: this.getCaasBadgeImageSrc(),
      iconBadgeIconName: this.getCaasBadgeIcon(),
      imageBadgeTitle: this.getCaasBadgeImageTitle(),
      showPlayButton: this.getCaasShowPlayButton(),
      showBadge: this.getCaasBadgeShowBadge(),
      actionType: this.getCaasActionType(),
      searchPlaceholder: this.getCaasSearchPlaceholder(),
      imageObjectPosition: 'right center',
    };

    this.safeCall(
      () => (this.content.headline = this.getCaasHeadline()),
      'Set headline'
    );
    this.safeCall(
      () => (this.content.paragraph = this.getCaasParagraph()),
      'Set paragraph'
    );
    this.safeCall(
      () => (this.content.imageSrc = this.getCaasSlot1Src()),
      'Set imageSrc'
    );
    this.safeCall(
      () => (this.content.imageSlot2Src = this.getCaasSlot2Src()),
      'Set imageSlot2Src'
    );
    this.safeCall(
      () => (this.content.imageSlot3Src = this.getCaasSlot3Src()),
      'Set imageSlot3Src'
    );
    this.safeCall(
      () => (this.content.imageBadgeSrc = this.getCaasBadgeImageSrc()),
      'Set imageBadgeSrc'
    );
    this.safeCall(
      () => (this.content.iconBadgeIconName = this.getCaasBadgeIcon()),
      'Set iconBadgeIconName'
    );
    this.safeCall(
      () => (this.content.showPlayButton = this.getCaasShowPlayButton()),
      'Set showPlayButton'
    );
    this.safeCall(
      () => (this.content.showBadge = this.getCaasBadgeShowBadge()),
      'Set showBadge'
    );
    this.safeCall(
      () => (this.content.actionType = this.getCaasActionType()),
      'Set actionType'
    );
    this.safeCall(
      () => (this.content.searchPlaceholder = this.getCaasSearchPlaceholder()),
      'Set searchPlaceholder'
    );
    this.safeCall(
      () => (this.content.type = this.getCaasBadgeVisualType()),
      'Set type'
    );
    this.safeCall(
      () => (this.content.videoId = this.getCaasVideoId()),
      'Set videoId'
    );
    this.safeCall(
      () => (this.content.videoSrc = this.getCaasVideoSrc()),
      'Set videoSrc'
    );
    this.safeCall(() => {
      this.content.videoTitle =
        this.caasData?.formData?.st_images_link?.value?.formData?.lt_link_title
          ?.value ?? '';
    }, 'Set videoTitle');

    this.safeCall(() => {
      Object.assign(this.content, this.extractLinkData());
    }, 'Set main link data');

    this.safeCall(() => {
      Object.assign(this.content, this.extractOptionalLinkData());
    }, 'Set optional link data');
  }

  private extractLinkData() {
    const link = this.caasData?.formData.st_link;
    return {
      buttonLabel: this.getCaasButtonLabel(link),
      buttonTitle: link?.value?.formData?.lt_link_title?.value ?? '',
      href: this.getCaasHref(link),
      hrefType: this.caasBodytextService.getHrefType(link as CaasLink),
      fragment: this.caasBodytextService.getFragment(link),
      target: this.getCaasTarget(link),
    };
  }

  private extractOptionalLinkData() {
    const link = this.caasData?.formData.st_link_optional;
    return {
      optionalButtonLabel: this.getCaasButtonLabel(link),
      optionalButtonTitle: link?.value?.formData?.lt_link_title?.value ?? '',
      optionalHref: this.getCaasHref(link),
      optionalFragment: this.caasBodytextService.getFragment(link),
      optionalTarget: this.getCaasTarget(link),
      optionalHrefType: this.caasBodytextService.getHrefType(link as CaasLink),
      optionalLinkType: this.caasBodytextService.checkLinksType(
        link as CaasLink
      ),
      optionalVideo: this.caasBodytextService.getVideo(
        link?.value?.formData?.lt_link_video_type as CaasFormField,
        link?.value?.formData?.lt_link_vimeo_id as CaasSimpleField,
        link?.value?.formData?.lt_link_youtube_id as CaasSimpleField
      ),
    };
  }
  /**
   * Safely calls a function and assigns its result to a variable, handling errors.
   * IMPORTANT: THIS MUST BE A PURE FUNCTION AND RETURN VOID. IF IT RETURNS GENERIC VALUES THE FUNCTION WON'T WORK AS EXPECTED.
   * @param fn The function to call.
   * @param label A label for the operation, used for error reporting.
   */
  private safeCall(fn: () => void, label: string): void {
    safeAssign(fn, label, this.errorHandler);
  }

  getCaasHeadline(): string {
    if (this.caasData?.formData.st_header?.value) {
      return this.caasData?.formData?.st_header?.value;
    }
    return '';
  }

  getCaasParagraph(): string {
    if (this.caasData?.formData.st_bodytext?.value) {
      return this.caasBodytextService.extractTextEditorContent(
        this.caasData?.formData.st_bodytext.value
      );
    }
    return '';
  }

  getCaasSlot1Src(): string {
    if (this.caasData?.formData.st_images?.value[0]?.formData?.st_image) {
      this.content.imageAlt = this.imagesService.getAltTextById(
        this.caasData?.formData.st_images.value[0].formData.st_image.value
          .identifier
      );
      this.content.imageTitle =
        this.caasData?.formData.st_images.value[0].formData?.st_image_title
          ?.value ?? '';
      return this.caasData?.formData.st_images.value[0].formData.st_image.value
        .url;
    }
    return '';
  }

  getCaasSlot2Src(): string | undefined {
    if (this.caasData?.formData.st_images?.value[1]?.formData?.st_image) {
      this.content.imageSlot2Alt = this.imagesService.getAltTextById(
        this.caasData?.formData.st_images.value[1].formData.st_image.value
          .identifier
      );
      this.content.imageSlot2Title =
        this.caasData?.formData.st_images.value[1].formData?.st_image_title
          ?.value ?? '';
      return this.caasData?.formData.st_images.value[1].formData.st_image.value
        .url;
    }
    return undefined;
  }

  getCaasSlot3Src(): string | undefined {
    if (this.caasData?.formData.st_images?.value[2]?.formData?.st_image) {
      this.content.imageSlot3Alt = this.imagesService.getAltTextById(
        this.caasData?.formData.st_images.value[2].formData.st_image.value
          .identifier
      );
      this.content.imageSlot3Title =
        this.caasData?.formData.st_images.value[2].formData?.st_image_title
          ?.value ?? '';
      return this.caasData?.formData.st_images.value[2].formData.st_image.value
        .url;
    }
    return undefined;
  }

  getCaasShowPlayButton(): boolean {
    if (this.caasData?.formData.st_images_link?.value) {
      return true;
    }
    return false;
  }

  getCaasBadgeIcon(): string | undefined {
    if (this.caasData?.formData.st_badge_icon?.value) {
      return this.caasData?.formData.st_badge_icon.value;
    }
    return undefined;
  }
  getCaasBadgeImageTitle(): string | undefined {
    if (this.caasData?.formData.st_badge_image_title?.value) {
      return this.caasData?.formData.st_badge_image_title.value;
    }
    return undefined;
  }
  getCaasBadgeImageSrc(): string | undefined {
    if (this.caasData?.formData.st_badge_image?.value?.url) {
      return this.caasData?.formData.st_badge_image.value.url;
    }
    return undefined;
  }
  getCaasBadgeShowBadge(): boolean {
    if (this.caasData?.formData.st_badge?.value) {
      return this.caasData?.formData.st_badge.value;
    }
    return false;
  }

  getCaasActionType(): ActionType | undefined {
    if (this.caasData?.formData.st_footer_type?.value) {
      switch (this.caasData?.formData.st_footer_type.value.label) {
        case 'Suche':
          return ActionType.SEARCH;
        case 'CTA':
          return ActionType.BUTTON;
        default:
          return undefined;
      }
    }
    return ActionType.BUTTON;
  }

  getCaasSearchPlaceholder(): string {
    if (this.caasData?.formData.st_inpagesearch_placeholder?.value) {
      return this.caasData?.formData.st_inpagesearch_placeholder.value;
    }
    return '';
  }

  getCaasBadgeVisualType(): 'icon' | '' {
    if (this.caasData?.formData.st_badge_visual_type?.value) {
      return this.caasData?.formData.st_badge_visual_type.value.identifier as
        | 'icon'
        | '';
    }
    return '';
  }

  getCaasButtonLabel(link?: CaasLink): string {
    if (link?.value) {
      return link.value.formData?.lt_link_label.value ?? '';
    }
    return '';
  }

  getCaasHref(link?: CaasLink): string {
    if (link?.value) {
      switch (link.value.formData?.lt_link_type.value.identifier) {
        case 'internal':
          return (
            this.caasRoutesService.getSeoRouteByIdentifier(
              link.value.formData?.lt_link_internal?.value?.identifier ?? '',
              this.router.config
            )?.path ?? '404'
          );
        case 'external':
        case 'download':
          return link.value.formData?.lt_link_external.value ?? '';
        default:
          return '';
      }
    }
    return '';
  }

  getCaasTarget(link?: CaasLink): string {
    if (link?.value) {
      switch (link.value.formData?.lt_link_type.value.identifier) {
        case 'internal':
          return '_self';
        case 'external':
        case 'download':
          return '_blank';
        default:
          return '_self';
      }
    }
    return '_self';
  }

  handleButtonClick($event: Event, target?: string, href?: string) {
    $event.preventDefault();
    switch (target) {
      case '_blank':
        if (typeof window !== 'undefined') {
          window.open(href, '_blank');
        }
        break;
      case '_self':
        this.router.navigate([href]);
        break;
      default:
        break;
    }
  }

  getCaasVideoId(): string {
    if (this.caasData?.formData.st_images_link?.value) {
      switch (
        this.caasData?.formData.st_images_link.value.formData
          ?.lt_link_video_type.value?.identifier
      ) {
        case 'youtube':
          return (
            this.caasData?.formData.st_images_link.value.formData
              ?.lt_link_youtube_id.value ?? ''
          );
        case 'vimeo':
          return (
            this.caasData?.formData.st_images_link.value.formData
              ?.lt_link_vimeo_id.value ?? ''
          );
        default:
          return '';
      }
    }
    return '';
  }

  getCaasVideoSrc(): SafeResourceUrl {
    if (this.caasData?.formData.st_images_link?.value) {
      switch (
        this.caasData?.formData.st_images_link.value.formData
          ?.lt_link_video_type.value?.identifier
      ) {
        case 'youtube':
          return this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://www.youtube.com/embed/${this.getCaasVideoId()}?h=4ca6a9fe01&badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1`
          );
        case 'vimeo':
          return this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://player.vimeo.com/video/${this.getCaasVideoId()}?h=4ca6a9fe01&badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1`
          );
        default:
          return '';
      }
    }
    return '';
  }
}
